import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { Row, Col, Form } from 'react-bootstrap';

/**
 * AgentPermissionForm component
 * Reusable form for managing agent permissions:
 * - Primary store assignment
 * - Delete permission toggle
 * - Multi-store view access
 */
export function AgentPermissionForm({ permissions, onChange, readOnlyPrimaryStore = false }) {
  const { apiCall } = useOutletContext();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async () => {
    try {
      const response = await apiCall(`${API_BASE_URL}/stores`);
      setStores(response.data || response || []);
    } catch (err) {
      console.error('Error loading stores:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStoreToggle = (storeId, checked) => {
    const current = permissions.view_store_ids || [];
    const updated = checked
      ? [...current, storeId]
      : current.filter(id => id !== storeId);

    onChange({
      ...permissions,
      view_store_ids: updated.length > 0 ? updated : null
    });
  };

  return (
    <>
      <hr className="my-3" />
      <h5 className="mb-3">Agent Permissions</h5>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Primary Store</Form.Label>
            <Form.Select
              value={permissions.store_id || ''}
              onChange={e => onChange({
                ...permissions,
                store_id: parseInt(e.target.value)
              })}
              required
              disabled={loading || readOnlyPrimaryStore}
            >
              <option value="">Select store...</option>
              {stores.map(store => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </Form.Select>
            <Form.Text>Agent's home store location</Form.Text>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Delete Permission</Form.Label>
            <Form.Check
              type="checkbox"
              label="Allow this agent to delete records (customers, vehicles, transactions, payments)"
              checked={permissions.can_delete || false}
              onChange={e => onChange({
                ...permissions,
                can_delete: e.target.checked
              })}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col>
          <Form.Group className="mb-3">
            <Form.Label>Additional Viewable Stores</Form.Label>
            <Form.Text className="d-block mb-2">
              Select additional stores this agent can view. If none selected, agent can only view their primary store.
            </Form.Text>
            {loading ? (
              <div>Loading stores...</div>
            ) : (
              <div
                style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: '1px solid #dee2e6',
                  padding: '10px',
                  borderRadius: '4px',
                  backgroundColor: '#f8f9fa'
                }}
              >
                {stores.map(store => (
                  <Form.Check
                    key={store.id}
                    type="checkbox"
                    id={`store-${store.id}`}
                    label={store.name}
                    checked={permissions.view_store_ids?.includes(store.id) || false}
                    onChange={e => handleStoreToggle(store.id, e.target.checked)}
                  />
                ))}
              </div>
            )}
          </Form.Group>
        </Col>
      </Row>
    </>
  );
}
